import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Verbal Fluency — composant jeu principal (FAS + semantic).
 *
 * Pattern minimal Sprint 2 unified : pas de LevelSelector ni
 * PedagogieDrawer. Niveau dérivé de localStorage progression simple.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { createVerbalFluencyAdapter, makeStimulus, } from './adapter';
import { Demo } from './demo';
import { VerbalFluencyDebriefView, VerbalFluencyStaircaseView, } from './staircaseView';
import { paradigmMeta, staircaseConfig, variantForLevel } from './params';
import { loadUserState } from '../../lib/user-state';
const GAME_SLUG = 'verbal-fluency';
function Intro({ level, onShowDemo, onSkipDemo }) {
    const variant = variantForLevel(level);
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "Voir un exemple" })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "D\u00E9marrer direct" })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-5 py-6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: paradigmMeta.paradigm }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: "Fluence verbale" }), _jsxs("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: ["Tape un maximum de mots", ' ', variant.mode === 'phonemic' ? 'commençant par la lettre demandée' : 'appartenant à la catégorie donnée', ' ', "en ", Math.round(variant.roundMs / 1000), " secondes."] }), _jsxs("div", { className: "grid w-full grid-cols-2 gap-3 border border-cyan-border-faint bg-cyan-surface p-4 text-start text-[12px] text-cyan-text-secondary", children: [_jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Niveau" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: [level, " ", variant.label] })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Round" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: [Math.round(variant.roundMs / 1000), " s"] })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Cible" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: [variant.targetScore, " mots"] })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Rounds" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: ["jusqu'\u00E0 ", staircaseConfig.maxTrials] })] })] }), _jsx(Link, { to: "/library", className: "text-center text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Retour aux jeux" })] }));
}
function IntroSlot({ level }) {
    const shell = useGameShellFromContext();
    return (_jsx(Intro, { level: level, onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo() }));
}
function DemoSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot() {
    const shell = useGameShellFromContext();
    useMemo(() => {
        try {
            window.localStorage.setItem(`labrain.${GAME_SLUG}.lastRun`, JSON.stringify({
                ts: Date.now(),
                threshold: shell.snapshot.staircase.thresholdEstimate,
            }));
        }
        catch {
            /* ignored */
        }
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
function deriveLang(i18nLang) {
    if (!i18nLang)
        return 'fr';
    return i18nLang.startsWith('en') ? 'en' : 'fr';
}
export function VerbalFluencyGame() {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lang = deriveLang(i18n.resolvedLanguage ?? i18n.language);
    const [level] = useState(1);
    const levelRef = useRef(level);
    levelRef.current = level;
    const langRef = useRef(lang);
    langRef.current = lang;
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createVerbalFluencyAdapter(level, lang);
    }
    const adapter = adapterRef.current;
    useEffect(() => {
        adapter.setLevel(level);
    }, [adapter, level]);
    useEffect(() => {
        adapter.setLang(lang);
    }, [adapter, lang]);
    const userSettings = useMemo(() => loadUserState().settings, []);
    const config = useMemo(() => ({
        gameSlug: GAME_SLUG,
        gameMaster: 'foudre',
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: userSettings.enableHaptics,
        enableSound: userSettings.enableSound,
        enableVisualFeedback: true,
    }), [userSettings.enableHaptics, userSettings.enableSound]);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => makeStimulus(engStim.trialIdx, engStim.value, levelRef.current, langRef.current), grader: (stim, resp) => ({
                    correct: resp.correct,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: {
                        words: resp.words,
                        wordCount: resp.wordCount,
                        mode: stim.prompt.mode,
                    },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, { level: level }), demo: _jsx(DemoSlot, {}), staircase: _jsx(VerbalFluencyStaircaseView, { adapter: adapter, level: level, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, {}), debrief: _jsx(VerbalFluencyDebriefView, { level: level, onAbort: () => navigate('/library') }), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default VerbalFluencyGame;
//# sourceMappingURL=game.js.map